import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface BrandLockupProps {
  /** When true, the wordmark + tag are hidden — leaving only the glyph (mobile / collapsed views). */
  glyphOnly?: boolean;
}

/**
 * Garden Art brand lockup for the top header.
 *
 * Three pieces:
 *  1. Botanical glyph (custom SVG — palm-frond + leaf abstraction in Najdi
 *     gold + evergreen). Hand-rolled so the bundle stays free of icon libs.
 *  2. Wordmark — "Garden Art" in the active locale (EN / AR via i18n).
 *  3. Subline — "ERP · WATAN FIRST" tag, micro-caps for credit + context.
 *
 * The lockup is its own component so the same identity can drop into the
 * AppHeader, the Landing hero, the print stylesheet, and the eventual
 * watan-handoff cover page without divergence.
 */
export default function BrandLockup({ glyphOnly = false }: BrandLockupProps) {
  const { t } = useTranslation();
  return (
    <Link to="/" className="ga-header-brand" aria-label={t('app.name')}>
      <svg
        className="ga-header-brand__glyph"
        viewBox="0 0 64 64"
        fill="none"
        role="img"
        aria-hidden="true"
      >
        {/* Outer ring — Najdi gold */}
        <circle cx="32" cy="32" r="29" stroke="#B8893E" strokeWidth="1.5" opacity="0.7" />
        {/* Central frond — evergreen, stylised palm shape */}
        <path
          d="M32 12c-2 8-2 14 0 30 2-16 2-22 0-30Z"
          fill="#1F4D3A"
          opacity="0.95"
        />
        {/* Side leaves — sand + gold accents */}
        <path
          d="M32 24c-8-2-14 2-18 6 6 0 12 2 18 6Z"
          fill="#3F8E5A"
          opacity="0.75"
        />
        <path
          d="M32 24c8-2 14 2 18 6-6 0-12 2-18 6Z"
          fill="#3F8E5A"
          opacity="0.75"
        />
        <path
          d="M32 36c-6-1-11 2-15 5 5 0 10 1 15 3Z"
          fill="#B8893E"
          opacity="0.6"
        />
        <path
          d="M32 36c6-1 11 2 15 5-5 0-10 1-15 3Z"
          fill="#B8893E"
          opacity="0.6"
        />
        {/* Base — earth dot */}
        <circle cx="32" cy="48" r="2.5" fill="#1A1F1B" opacity="0.5" />
      </svg>
      {!glyphOnly && (
        <span className="ga-header-brand__wordmark">
          <span className="ga-header-brand__primary">{t('app.name')}</span>
          <span className="ga-header-brand__tag">{t('app.tagline')}</span>
        </span>
      )}
    </Link>
  );
}
